<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import NativeSelect from 'dashboard/components-next/native-select/NativeSelect.vue';
import AgentSelectDropdown from 'dashboard/components-next/agent/AgentSelectDropdown.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const emit = defineEmits(['update']);

const store = useStore();
const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const aiAgents = useMapGetter('aiAgents/getAgents');
const labels = useMapGetter('labels/getLabels');
const lifecycleStages = useMapGetter('lifecycleStages/getStages');
const lifecycleSettings = useMapGetter('lifecycleStages/getSettings');

const state = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  lifecycleStageId: '',
});

const showTagsDropdown = ref(false);
const selectedLabels = ref([]);
const selectedAgentId = ref(null);
const selectedAiAgentId = ref(null);

const isLifecycleVisible = computed(
  () =>
    lifecycleSettings.value.enabled !== false &&
    lifecycleStages.value.length > 0
);

const lifecycleOptions = computed(() =>
  lifecycleStages.value.map(stage => ({
    value: String(stage.id),
    label: `${stage.icon} ${stage.name}`,
  }))
);

const contactInputClass =
  '!h-9 !rounded-xl !bg-transparent !text-[13px] !px-3 !py-1';
const contactFieldLabelClass =
  'mb-0 block pl-0.5 text-[12px] font-semibold text-n-slate-11';
const contactFieldLabelWithIconClass =
  'mb-0 flex items-center gap-1 pl-0.5 text-[12px] font-semibold text-n-slate-11';

const labelMenuItems = computed(() =>
  labels.value
    .map(label => ({
      label: label.title,
      value: label.id,
      thumbnail: { name: label.title, color: label.color },
      color: label.color,
      badgeEmoji: label.emoji,
      isSelected: selectedLabels.value.some(item => item.id === label.id),
      action: 'contactLabel',
    }))
    .toSorted((a, b) => {
      if (a.isSelected !== b.isSelected) {
        return Number(b.isSelected) - Number(a.isSelected);
      }
      return a.label.localeCompare(b.label);
    })
);

function isValidEmail(value) {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function buildAssignmentPayload() {
  if (selectedAiAgentId.value) {
    return {
      assigneeId: selectedAiAgentId.value,
      assigneeType: 'AgentBot',
    };
  }

  if (selectedAgentId.value) {
    return {
      assigneeId: selectedAgentId.value,
      assigneeType: 'User',
    };
  }

  return {};
}

const formPayload = computed(() => {
  const lifecycleStageId =
    state.lifecycleStageId === '' ? null : Number(state.lifecycleStageId);
  const assignmentPayload = buildAssignmentPayload();

  return {
    name: `${state.firstName} ${state.lastName}`.trim(),
    firstName: state.firstName,
    lastName: state.lastName,
    email: state.email,
    phoneNumber: state.phoneNumber,
    lifecycleStageId,
    labels: selectedLabels.value.map(label => label.title),
    ...assignmentPayload,
  };
});

const isFormInvalid = computed(() => {
  return !state.firstName.trim() || !isValidEmail(state.email.trim());
});

function resetForm() {
  state.firstName = '';
  state.lastName = '';
  state.email = '';
  state.phoneNumber = '';
  state.lifecycleStageId = '';
  selectedAgentId.value = null;
  selectedAiAgentId.value = null;
  selectedLabels.value = [];
  showTagsDropdown.value = false;
}

function toggleLabel({ value }) {
  const selectedLabel = labels.value.find(label => label.id === value);
  if (!selectedLabel) return;

  const isSelected = selectedLabels.value.some(label => label.id === value);
  selectedLabels.value = isSelected
    ? selectedLabels.value.filter(label => label.id !== value)
    : [...selectedLabels.value, selectedLabel];
}

function removeLabel(labelToRemove) {
  selectedLabels.value = selectedLabels.value.filter(
    label => label.id !== labelToRemove.id
  );
}

function closeTagsDropdown() {
  showTagsDropdown.value = false;
}

function toggleTagsDropdown() {
  showTagsDropdown.value = !showTagsDropdown.value;
}

function getLabelBadgeStyle(color) {
  return {
    backgroundColor: `${color}26`,
    outlineColor: color,
  };
}

watch(
  formPayload,
  payload => {
    emit('update', payload);
  },
  { immediate: true }
);

onMounted(() => {
  if (!agents.value.length) {
    store.dispatch('agents/get');
  }
  if (!aiAgents.value.length) {
    store.dispatch('aiAgents/get');
  }
  if (!labels.value.length) {
    store.dispatch('labels/get');
  }
  if (!lifecycleStages.value.length) {
    store.dispatch('lifecycleStages/get');
  }
});

defineExpose({
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex h-full flex-col">
    <div class="border-b border-n-weak px-4 py-5">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_DETAILS') }}
          </h2>
        </div>
        <AgentSelectDropdown
          v-model="selectedAgentId"
          v-model:ai-agent-model-value="selectedAiAgentId"
          :agents="agents"
          :ai-agents="aiAgents"
          :fallback-agent="currentUser"
          :placeholder="t('CONTACT_PANEL.NOT_AVAILABLE')"
          button-class="flex h-5 items-center gap-1 rounded-full border border-n-weak bg-n-surface-1 px-1.5 text-[11px] text-n-slate-12"
          name-class="truncate text-[15px] font-semibold leading-none"
          name-max-width-class="max-w-[118px]"
          :ai-agents-title="t('CHAT_LIST.OMNI_SIDEBAR.AI_AGENTS')"
          dropdown-class="right-0 top-full mt-2 w-[272px]"
          :avatar-size="24"
          :thumbnail-size="24"
        />
      </div>

      <div class="mt-3 space-y-4">
        <label class="block">
          <span :class="contactFieldLabelWithIconClass">
            <span>{{
              $t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.FIRST_NAME')
            }}</span>
            <span
              class="i-lucide-asterisk size-3 text-n-ruby-9"
              aria-hidden="true"
            />
          </span>
          <Input
            v-model="state.firstName"
            :placeholder="$t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.FIRST_NAME')"
            :custom-input-class="contactInputClass"
          />
        </label>

        <label class="block">
          <span :class="contactFieldLabelClass">
            {{ $t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.LAST_NAME') }}
          </span>
          <Input
            v-model="state.lastName"
            :placeholder="$t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.LAST_NAME')"
            :custom-input-class="contactInputClass"
          />
        </label>

        <label class="block">
          <span :class="contactFieldLabelClass">
            {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') }}
          </span>
          <PhoneNumberInput
            v-model="state.phoneNumber"
            :placeholder="$t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.PHONE_NUMBER')"
          />
        </label>

        <label class="block">
          <span :class="contactFieldLabelClass">
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}
          </span>
          <Input
            v-model="state.email"
            type="email"
            :placeholder="$t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.EMAIL_ADDRESS')"
            :message="
              state.email && !isValidEmail(state.email.trim())
                ? $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR')
                : ''
            "
            :message-type="
              state.email && !isValidEmail(state.email.trim())
                ? 'error'
                : 'info'
            "
            :custom-input-class="contactInputClass"
          />
        </label>

        <label v-if="isLifecycleVisible" class="block">
          <span :class="contactFieldLabelClass">
            {{ $t('CONTACTS_LAYOUT.TABLE.LIFECYCLE') }}
          </span>
          <NativeSelect v-model="state.lifecycleStageId">
            <option value="">
              {{ $t('CONTACTS_LAYOUT.NEW_CONTACT_PANEL.LIFECYCLE') }}
            </option>
            <option
              v-for="stage in lifecycleOptions"
              :key="stage.value"
              :value="stage.value"
            >
              {{ stage.label }}
            </option>
          </NativeSelect>
        </label>
      </div>
    </div>

    <div class="flex-1 px-4 py-5">
      <div class="flex items-center justify-between gap-3">
        <h3 class="text-xl font-semibold text-n-slate-12">
          {{ $t('CONTACTS_LAYOUT.TABLE.LABELS') }}
        </h3>

        <OnClickOutside @trigger="closeTagsDropdown">
          <div class="relative">
            <button
              type="button"
              class="flex size-8 items-center justify-center rounded-xl border border-n-weak text-n-slate-12 transition-colors hover:bg-n-alpha-2"
              :class="{ 'bg-n-alpha-2': showTagsDropdown }"
              @click="toggleTagsDropdown"
            >
              <span
                class="i-lucide-plus size-5 transition-transform duration-150"
                :class="{ 'rotate-45': showTagsDropdown }"
              />
            </button>

            <transition
              enter-active-class="transition duration-150 ease-out"
              enter-from-class="translate-y-1 scale-95 opacity-0"
              enter-to-class="translate-y-0 scale-100 opacity-100"
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="translate-y-0 scale-100 opacity-100"
              leave-to-class="translate-y-1 scale-95 opacity-0"
            >
              <DropdownMenu
                v-if="showTagsDropdown"
                :menu-items="labelMenuItems"
                show-search
                :search-placeholder="
                  t('CONTACT_PANEL.LABELS.LABEL_SELECT.SEARCH_AND_SELECT')
                "
                label-class="hidden"
                class="right-0 top-full mt-2 max-h-80 w-72 overflow-y-auto"
                @action="toggleLabel"
              >
                <template #thumbnail="{ item }">
                  <div class="flex min-w-0 items-center gap-2">
                    <span
                      class="flex size-5 shrink-0 items-center justify-center rounded-md border text-[13px] transition-colors"
                      :class="
                        item.isSelected
                          ? 'border-n-brand bg-n-brand text-white'
                          : 'border-n-strong bg-n-surface-1 text-transparent'
                      "
                    >
                      <span class="i-lucide-check size-3.5" />
                    </span>
                    <span
                      class="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm font-semibold text-n-slate-12 outline outline-1 -outline-offset-1"
                      :style="getLabelBadgeStyle(item.color)"
                    >
                      <span
                        v-if="item.badgeEmoji"
                        class="text-base leading-none"
                      >
                        {{ item.badgeEmoji }}
                      </span>
                      <span class="truncate">{{ item.label }}</span>
                    </span>
                  </div>
                </template>
              </DropdownMenu>
            </transition>
          </div>
        </OnClickOutside>
      </div>

      <div class="mt-4 flex min-h-20 flex-wrap items-start gap-2">
        <div
          v-for="label in selectedLabels"
          :key="label.id"
          class="group inline-flex max-w-full items-center gap-1.5 rounded-lg px-2.5 py-1 outline outline-1 -outline-offset-1"
          :style="getLabelBadgeStyle(label.color)"
        >
          <span v-if="label.emoji" class="text-base leading-none">{{
            label.emoji
          }}</span>
          <span
            class="max-w-[160px] truncate text-sm font-semibold text-n-slate-12"
          >
            {{ label.title }}
          </span>
          <button
            type="button"
            class="flex size-4 shrink-0 items-center justify-center rounded text-n-slate-11 opacity-0 transition-opacity hover:bg-n-alpha-2 group-hover:opacity-100"
            @click="removeLabel(label)"
          >
            <span class="i-lucide-x size-3" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
